import {
  precomputeGraphScores,
  getSubjectPack,
  defaultSubjectId,
  createPlayer,
  applyAnswer,
  withUpdatedQuestion,
  createMasteryState,
  updateMasteryAfterAttempt,
  generateMcqFromConcept,
  currentStreakDays,
  recordGameEvent,
  getGameMaster,
  normalizeGameMasterId,
  GAME_MASTER_IDS,
  SUBJECT_IDS,
  type GameMaster,
  type GameMasterId,
  type MasteryState,
  type GameEvent,
  type SubjectPack,
  getLinearQuestProgress,
} from "@engine/index.js";
import { LocalStoragePersistence } from "@engine/persistence/localStorage.js";
import { SupabasePersistence } from "@engine/persistence/supabase.js";
import type { EnginePersistence } from "@engine/persistence/types.js";
import type { PwaView } from "@engine/subjects/ai_literacy/subjectPack.js";
import type { Session } from "@supabase/supabase-js";
import {
  runAllMigrations,
  loadActiveCharacterId,
  saveActiveCharacterId,
  loadActiveSubjectId,
  saveActiveSubjectId,
  storagePrefix,
  setActiveStoragePrefix,
  readCharacterPreferencesJson,
  writeCharacterPreferencesJson,
  CHARACTER_PREFS_KEY,
  DEFAULT_CHARACTER_ID,
} from "./subjectConfig.js";
import type { CharacterPreferences } from "./character.js";
import type { Locale } from "@engine/generate/conceptCopy.js";
import type { PlayerState, Question } from "@engine/types.js";
import { mountBackpack } from "./games/backpack/Backpack.js";
import { mountSpotLie } from "./games/spotlie/SpotLie.js";
import { mountRedact } from "./games/redact/Redact.js";
import { pickRoundIndexByMastery } from "./games/pickRoundByMastery.js";
import { advanceRound } from "./games/rotation.js";
import { getPwaUi } from "./i18n/pwaUi.js";
import { getSupabase } from "./auth/supabaseClient.js";
import {
  consumeAuthHashError,
  getCurrentSession,
  initSessionStore,
  signOut as authSignOut,
  subscribeToSession,
} from "./auth/session.js";
import { mountSignInModal } from "./auth/SignInModal.js";
import { maybeRunSilentMigration } from "./auth/migrate.js";
import { SnapshotPersistence } from "./persistence/snapshot.js";
import { mountPrivacyView } from "./legal/PrivacyView.js";
import { mountAboutView } from "./about/AboutView.js";
import { mountSettingsView } from "./settings/SettingsView.js";
import { mountDeleteAccountModal } from "./settings/DeleteAccountModal.js";

runAllMigrations();

const SESSION_LENGTH = 10;
const GENERATED_PER_CONCEPT = 3;

type View = PwaView | "privacy" | "settings" | "about";

const recents: string[] = [];

let pack: SubjectPack =
  getSubjectPack(loadActiveSubjectId()) ?? getSubjectPack(defaultSubjectId())!;
let g = pack.buildGraph();
let scores = precomputeGraphScores(g);

const characterId = loadActiveCharacterId();
let persistencePrefix = storagePrefix(characterId, pack.id);
setActiveStoragePrefix(persistencePrefix);

const sessionKey = (): string => persistencePrefix + "browser-session";
const viewKey = (): string => persistencePrefix + "browser-view";

let pers: EnginePersistence = new LocalStoragePersistence(persistencePrefix);

let characterPreferences: CharacterPreferences = readCharacterPreferencesJson(
  persistencePrefix,
  pack.defaultLocale,
  pack.id
);
if (typeof localStorage !== "undefined") {
  if (localStorage.getItem(persistencePrefix + CHARACTER_PREFS_KEY) == null) {
    writeCharacterPreferencesJson(persistencePrefix, characterPreferences);
  }
}

type SessionState = {
  total: number;
  correct: number;
  startedAt: string;
  conceptIds: string[];
};

function loadMasteryState(): MasteryState {
  const fromPersistence = pers.loadMastery?.() ?? null;
  if (!fromPersistence) return createMasteryState(pack.conceptIds);
  const base = createMasteryState(pack.conceptIds);
  return {
    defaultRating: fromPersistence.defaultRating ?? base.defaultRating,
    byConcept: { ...base.byConcept, ...fromPersistence.byConcept },
  };
}

function saveMasteryState(masteryState: MasteryState): void {
  pers.saveMastery?.(masteryState);
}

function loadSession(): SessionState {
  if (typeof localStorage === "undefined") return freshSession();
  const s = localStorage.getItem(sessionKey());
  if (!s) return freshSession();
  try {
    return JSON.parse(s) as SessionState;
  } catch {
    return freshSession();
  }
}

function saveSession(s: SessionState): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(sessionKey(), JSON.stringify(s));
  }
}

function loadView(): View {
  if (typeof localStorage === "undefined") return "home";
  const v = localStorage.getItem(viewKey());
  if (v === "mcq" || v === "backpack" || v === "spotlie" || v === "redact" || v === "home") {
    return v;
  }
  return "home";
}

function saveView(v: View): void {
  if (typeof localStorage === "undefined") return;
  // Hash-routed views (privacy/settings) are transient navigation, not learning
  // state; keep them out of localStorage so a refresh from any other device or
  // window starts at the last learning view.
  if (v === "privacy" || v === "settings" || v === "about") return;
  localStorage.setItem(viewKey(), v);
}

const HASH_VIEWS = {
  "#/privacy": "privacy",
  "#/settings": "settings",
  "#/about": "about",
} as const;

type HashView = "privacy" | "settings" | "about";

function readHashView(): HashView | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  for (const [prefix, view] of Object.entries(HASH_VIEWS)) {
    if (hash === prefix || hash.startsWith(prefix + "?") || hash.startsWith(prefix + "/")) {
      return view as HashView;
    }
  }
  return null;
}

function writeHashFor(v: View): void {
  if (typeof window === "undefined") return;
  const desired =
    v === "privacy"
      ? "#/privacy"
      : v === "settings"
        ? "#/settings"
        : v === "about"
          ? "#/about"
          : "";
  if (window.location.hash === desired) return;
  if (desired) {
    window.history.replaceState(null, "", desired);
  } else if (window.location.hash) {
    // Clearing a hash with replaceState avoids polluting history with empty entries.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

function saveLocale(locale: Locale): void {
  characterPreferences = { ...characterPreferences, locale, subjectId: pack.id };
  writeCharacterPreferencesJson(persistencePrefix, characterPreferences);
}

function freshSession(): SessionState {
  return { total: 0, correct: 0, startedAt: new Date().toISOString(), conceptIds: [] };
}

/** Mulberry32-style cheap PRNG so generated questions are stable across reloads. */
function seededRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let appLocale: Locale = characterPreferences.locale;

function currentGm(): GameMaster {
  return getGameMaster(normalizeGameMasterId(characterPreferences.gmId));
}

function buildBlendedBank(
  persisted: Question[] | null,
  options: { locale?: Locale; gm?: GameMaster } = {}
): Question[] {
  const locale = options.locale ?? appLocale;
  const gm = options.gm ?? currentGm();

  const seedCanonById = new Map(pack.seedQuestions.map((q) => [q.id, q]));
  function localizeCanonicalSeed(canon: Question, ratingOverride?: number): Question {
    const localized = pack.applySeedLocale ? pack.applySeedLocale(canon, locale) : canon;
    const rating = ratingOverride !== undefined ? ratingOverride : canon.rating;
    if (rating === localized.rating) return localized;
    return { ...localized, rating };
  }

  const baseRatings = [1100, 1300, 1500];
  const packCopy = pack.getCopy(locale);
  const generated: Question[] = [];
  let i = 0;
  for (const conceptId of pack.conceptIds) {
    for (let k = 0; k < GENERATED_PER_CONCEPT; k++) {
      const rating = baseRatings[k % baseRatings.length]!;
      const rng = seededRng(0x9e3779b1 ^ ((i + 1) * 2654435761));
      const distractorMode = gm.distractorForGenerated(rating);
      generated.push(
        generateMcqFromConcept(conceptId, {
          rating,
          rng,
          id: `g-${conceptId}-${k}`,
          graph: g,
          locale,
          distractorMode,
          copy: packCopy,
        })
      );
      i++;
    }
  }
  if (!persisted) {
    const seeds = pack.seedQuestions.map((q) => localizeCanonicalSeed(q));
    return [...seeds, ...generated];
  }
  const relocalizedPersisted = persisted.map((q) => {
    if (q.source !== "seed") return q;
    const canon = seedCanonById.get(q.id);
    if (!canon) return q;
    return localizeCanonicalSeed(canon, q.rating);
  });
  const persistedIds = new Set(persisted.map((q) => q.id));
  const newGenerated = generated.filter((q) => !persistedIds.has(q.id));
  return [...relocalizedPersisted, ...newGenerated];
}

function setLocale(next: Locale): void {
  if (next === appLocale) return;
  appLocale = next;
  saveLocale(next);
  const kept = bank.filter((q) => q.source === "seed");
  bank = buildBlendedBank(kept.length > 0 ? kept : null, { locale: next, gm: currentGm() });
  pers.saveQuestionBank(bank);
  current = null;
  selectionLocked = false;
  render();
}

function wireLocaleSelect(select: HTMLSelectElement | null): void {
  if (!select) return;
  select.replaceChildren();
  for (const loc of pack.supportedLocales) {
    const o = document.createElement("option");
    o.value = loc;
    o.textContent = loc === "en" ? "English" : loc === "es" ? "Español" : "Ελληνικά";
    if (loc === appLocale) o.selected = true;
    select.appendChild(o);
  }
  select.onchange = () => {
    const v = select.value;
    if (v === "en" || v === "es" || v === "el") setLocale(v);
  };
}

function rebindPersistenceForSubject(): void {
  const cid = loadActiveCharacterId();
  persistencePrefix = storagePrefix(cid, pack.id);
  setActiveStoragePrefix(persistencePrefix);
}

async function setSubjectId(next: string): Promise<void> {
  const p = getSubjectPack(next);
  if (!p || p.id === pack.id) return;
  pack = p;
  g = pack.buildGraph();
  scores = precomputeGraphScores(g);
  rebindPersistenceForSubject();
  saveActiveSubjectId(pack.id);
  const keepLocale = appLocale;
  characterPreferences = readCharacterPreferencesJson(persistencePrefix, keepLocale, pack.id);
  appLocale = characterPreferences.locale;
  characterPreferences = { ...characterPreferences, subjectId: pack.id, locale: appLocale };
  writeCharacterPreferencesJson(persistencePrefix, characterPreferences);
  await rebuildPersistence(getCurrentSession());
  render();
}

function wireSubjectSelect(select: HTMLSelectElement | null): void {
  if (!select) return;
  const ui = getPwaUi(appLocale);
  select.replaceChildren();
  const optLabel: Record<string, string> = {
    ai_literacy: ui.subjectOptionAiLiteracy,
    first_aid: ui.subjectOptionFirstAid,
    notany: ui.subjectOptionNotany,
    epso_ad8_ai: ui.subjectOptionEpsoAd8Ai,
  };
  for (const id of SUBJECT_IDS) {
    if (!getSubjectPack(id)) continue;
    const o = document.createElement("option");
    o.value = id;
    o.textContent = optLabel[id] ?? id;
    if (id === pack.id) o.selected = true;
    select.appendChild(o);
  }
  select.onchange = () => {
    void setSubjectId(select.value);
  };
}

function wireProfileSelect(select: HTMLSelectElement | null): void {
  if (!select) return;
  const active = loadActiveCharacterId();
  select.replaceChildren();
  const o = document.createElement("option");
  o.value = DEFAULT_CHARACTER_ID;
  o.textContent = getPwaUi(appLocale).profileOptionGuest;
  if (active === DEFAULT_CHARACTER_ID) o.selected = true;
  select.appendChild(o);
  select.onchange = () => {
    if (select.value === DEFAULT_CHARACTER_ID) {
      saveActiveCharacterId(DEFAULT_CHARACTER_ID);
    }
  };
}

function wireGmSelect(select: HTMLSelectElement | null): void {
  if (!select) return;
  const ui = getPwaUi(appLocale);
  const cur = normalizeGameMasterId(characterPreferences.gmId);
  select.replaceChildren();
  const labels: Record<GameMasterId, string> = {
    default: ui.gmDefault,
    patient: ui.gmPatient,
    drill: ui.gmDrill,
  };
  for (const id of GAME_MASTER_IDS) {
    const o = document.createElement("option");
    o.value = id;
    o.textContent = labels[id];
    if (id === cur) o.selected = true;
    select.appendChild(o);
  }
  select.onchange = () => {
    setGameMasterId(normalizeGameMasterId(select.value));
  };
}

function setGameMasterId(id: GameMasterId): void {
  if (normalizeGameMasterId(characterPreferences.gmId) === id) return;
  characterPreferences = { ...characterPreferences, gmId: id, subjectId: pack.id };
  writeCharacterPreferencesJson(persistencePrefix, characterPreferences);
  const kept = bank.filter((q) => q.source === "seed");
  bank = buildBlendedBank(kept.length > 0 ? kept : null, {
    locale: appLocale,
    gm: getGameMaster(id),
  });
  pers.saveQuestionBank(bank);
  recents.length = 0;
  current = null;
  selectionLocked = false;
  render();
}

// State is initialised to safe defaults at module-top so the JS module *always*
// finishes evaluating, even if every persistence call below throws. Real values
// are loaded inside `boot()` via `loadPersistedState()`, where each step is
// wrapped in try/catch. Without this split, a single throw from a corrupted
// localStorage value (e.g. `QuotaExceededError` on a near-full Android Chrome
// profile, truncated JSON, third-party-cookie blocking) blanked the page on
// every refresh — `boot()` (and its synchronous `render()`) never ran.
let player: PlayerState = createPlayer(1200);
let bank: Question[] = [...pack.seedQuestions];
let mastery: MasteryState = createMasteryState(pack.conceptIds);
let session: SessionState = freshSession();
let currentView: View = "home";
let current: Question | null = null;
let selectionLocked = false;
let showSummaryNext = false;
let unmountCurrentGame: (() => void) | null = null;

type MiniGameEndStats = {
  gameType: "backpack" | "spotlie" | "redact";
  perfectRounds: number;
  weightedAccuracy: number;
  conceptIds: readonly string[];
};
/** Set after the last mini-game round; cleared on new session, navigation away, or reset. */
let miniGameEndStats: MiniGameEndStats | null = null;
let unmountSignInModal: (() => void) | null = null;
let pendingAuthError: string | null = null;
let signedOutManually = false;
let snapshot: SnapshotPersistence | null = null;
let rebuildNonce = 0;
let snapshotWriteFailureListenerBound = false;

function showToast(message: string): void {
  const existing = document.querySelectorAll(".app-toast");
  for (const node of existing) node.remove();
  const toast = document.createElement("div");
  toast.className = "app-toast";
  toast.textContent = message;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3500);
}

async function rebuildPersistence(authSession: Session | null): Promise<void> {
  const nonce = ++rebuildNonce;
  if (snapshot) {
    const currentSnapshot = snapshot;
    snapshot = null;
    await currentSnapshot.close();
  }
  if (nonce !== rebuildNonce) return;
  if (authSession) {
    const remote = new SupabasePersistence({
      client: getSupabase(),
      userId: authSession.user.id,
      subjectId: pack.id,
    });
    const migration = await maybeRunSilentMigration(remote, persistencePrefix);
    if (migration.migrated) {
      showToast(getPwaUi(appLocale).authMigratedToast);
    }
    const hydrated = await SnapshotPersistence.hydrate(remote);
    if (nonce !== rebuildNonce) {
      await hydrated.close();
      return;
    }
    snapshot = hydrated;
    pers = snapshot;
    signedOutManually = false;
  } else {
    pers = new LocalStoragePersistence(persistencePrefix);
    if (signedOutManually) {
      currentView = "home";
      saveView("home");
      writeHashFor("home");
    }
  }
  player = pers.loadPlayer() ?? createPlayer(1200);
  bank = buildBlendedBank(pers.loadQuestionBank());
  pers.saveQuestionBank(bank);
  mastery = loadMasteryState();
  session = loadSession();
  recents.length = 0;
  current = null;
  selectionLocked = false;
  showSummaryNext = false;
}

function setView(v: View): void {
  if (unmountCurrentGame) {
    unmountCurrentGame();
    unmountCurrentGame = null;
  }
  if (
    miniGameEndStats &&
    (v === "home" ||
      v === "mcq" ||
      v === "privacy" ||
      v === "settings" ||
      v !== miniGameEndStats.gameType)
  ) {
    miniGameEndStats = null;
  }
  currentView = v;
  saveView(v);
  writeHashFor(v);
  current = null;
  selectionLocked = false;
  showSummaryNext = false;
  render();
}

function openSignInModal(initialError?: string): void {
  if (unmountSignInModal) return;
  const root = document.getElementById("app") ?? document.body;
  unmountSignInModal = mountSignInModal(
    root,
    appLocale,
    () => {
      unmountSignInModal = null;
      render();
    },
    initialError ?? pendingAuthError ?? undefined
  );
  pendingAuthError = null;
}

function renderAuthHeader(host: HTMLElement): void {
  const ui = getPwaUi(appLocale);
  const session = getCurrentSession();
  const header = document.createElement("div");
  header.className = "auth-header";
  if (session) {
    const email = session.user?.email ?? "";
    header.innerHTML = `
      <span class="auth-badge">${escapeHtml(ui.authSignedInAs.replace("{email}", email))}</span>
      <button type="button" class="auth-settings">${escapeHtml(ui.authSettings)}</button>
      <button type="button" class="auth-signout">${escapeHtml(ui.authSignOut)}</button>
    `;
    (header.querySelector(".auth-settings") as HTMLButtonElement).onclick = () =>
      setView("settings");
    (header.querySelector(".auth-signout") as HTMLButtonElement).onclick = () => {
      signedOutManually = true;
      void authSignOut();
    };
  } else {
    header.innerHTML = `<button type="button" class="auth-signin">${escapeHtml(ui.authSignIn)}</button>`;
    (header.querySelector(".auth-signin") as HTMLButtonElement).onclick = () => {
      openSignInModal();
    };
  }
  host.prepend(header);
}

function render(): void {
  const root = document.getElementById("app");
  if (!root) return;
  const decks = pack.localizeDecks ? pack.localizeDecks(appLocale) : pack.decks;
  switch (currentView) {
    case "home":
      renderHome(root);
      return;
    case "mcq":
      renderMcq(root);
      return;
    case "backpack":
      if (miniGameEndStats?.gameType === "backpack") {
        renderMiniGameSummary(root, miniGameEndStats);
        return;
      }
      mountGame(root, "backpack", decks.backpack.length, (slot, ctx, ri) =>
        mountBackpack(slot, ctx, ri, decks.backpack)
      );
      return;
    case "spotlie":
      if (miniGameEndStats?.gameType === "spotlie") {
        renderMiniGameSummary(root, miniGameEndStats);
        return;
      }
      mountGame(root, "spotlie", decks.spotlie.length, (slot, ctx, ri) =>
        mountSpotLie(slot, ctx, ri, decks.spotlie)
      );
      return;
    case "redact":
      if (miniGameEndStats?.gameType === "redact") {
        renderMiniGameSummary(root, miniGameEndStats);
        return;
      }
      mountGame(root, "redact", decks.redact.length, (slot, ctx, ri) =>
        mountRedact(slot, ctx, ri, decks.redact)
      );
      return;
    case "privacy":
      renderPrivacy(root);
      return;
    case "settings":
      renderSettings(root);
      return;
    case "about":
      renderAbout(root);
      return;
  }
}

function renderFooter(host: HTMLElement): void {
  const ui = getPwaUi(appLocale);
  const footer = document.createElement("footer");
  footer.className = "app-footer";

  const aboutLink = document.createElement("a");
  aboutLink.href = "#/about";
  aboutLink.className = "app-footer-link";
  aboutLink.textContent = ui.footerAboutLink;
  aboutLink.addEventListener("click", (e) => {
    e.preventDefault();
    setView("about");
  });
  footer.appendChild(aboutLink);

  const link = document.createElement("a");
  link.href = "#/privacy";
  link.className = "app-footer-link";
  link.textContent = ui.footerPrivacyLink;
  link.addEventListener("click", (e) => {
    e.preventDefault();
    setView("privacy");
  });
  footer.appendChild(link);
  host.appendChild(footer);
}

function openDeleteAccountModal(): void {
  const session = getCurrentSession();
  if (!session) return;
  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-account`;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
  mountDeleteAccountModal({
    locale: appLocale,
    accessToken: session.access_token,
    functionUrl,
    anonKey,
    onClose: () => {},
    onSuccess: () => {
      void (async () => {
        try {
          if (typeof localStorage !== "undefined") {
            for (const k of Object.keys(localStorage)) {
              if (k.startsWith("notanyai:") || k.startsWith("ai-prepper:")) {
                localStorage.removeItem(k);
              }
            }
          }
          await authSignOut();
        } finally {
          signedOutManually = true;
          showToast(getPwaUi(appLocale).authDeletedToast);
          setView("home");
        }
      })();
    },
  });
}

function renderSettings(root: HTMLElement): void {
  const session = getCurrentSession();
  if (!session) {
    setView("home");
    return;
  }
  root.innerHTML = "";
  renderAuthHeader(root);
  const wrap = document.createElement("div");
  root.appendChild(wrap);
  const ui = getPwaUi(appLocale);
  const gmLabels: Record<string, string> = {
    default: ui.gmDefault,
    patient: ui.gmPatient,
    drill: ui.gmDrill,
  };
  const gmName = gmLabels[normalizeGameMasterId(characterPreferences.gmId)] ?? ui.gmDefault;
  const subjectLabels: Record<string, string> = {
    ai_literacy: ui.subjectOptionAiLiteracy,
    first_aid: ui.subjectOptionFirstAid,
    notany: ui.subjectOptionNotany,
    epso_ad8_ai: ui.subjectOptionEpsoAd8Ai,
  };
  const subjectName = subjectLabels[pack.id] ?? pack.id;
  mountSettingsView(wrap, {
    email: session.user?.email ?? "",
    locale: appLocale,
    gmName,
    subjectName,
    onBackHome: () => setView("home"),
    onDeleteAccountClick: openDeleteAccountModal,
  });
  renderFooter(root);
}

function renderPrivacy(root: HTMLElement): void {
  root.innerHTML = "";
  renderAuthHeader(root);
  const wrap = document.createElement("div");
  wrap.className = "card privacy-card";
  root.appendChild(wrap);
  mountPrivacyView(wrap, { locale: appLocale, onBackHome: () => setView("home") });
  renderFooter(root);
}

function renderAbout(root: HTMLElement): void {
  root.innerHTML = "";
  renderAuthHeader(root);
  const wrap = document.createElement("div");
  wrap.className = "card about-card";
  root.appendChild(wrap);
  mountAboutView(wrap, { locale: appLocale, onBackHome: () => setView("home") });
  renderFooter(root);
}

type GameContext = { emit: (event: GameEvent) => void; done: () => void };
type MountFn = (root: HTMLElement, ctx: GameContext, roundIndex: number) => () => void;

function gameRoundConceptIds(gameType: "backpack" | "spotlie" | "redact", index: number): string[] {
  const decks = pack.localizeDecks ? pack.localizeDecks(appLocale) : pack.decks;
  if (gameType === "backpack") {
    const r = decks.backpack[index];
    return r ? [...new Set(r.chips.map((c) => c.conceptId))] : [];
  }
  if (gameType === "spotlie") {
    const r = decks.spotlie[index];
    return r ? [...new Set(r.claims.map((c) => c.conceptId))] : [];
  }
  const r = decks.redact[index];
  return r ? [...new Set(r.tokens.map((t) => t.conceptId))] : [];
}

function mountGame(
  root: HTMLElement,
  gameType: "backpack" | "spotlie" | "redact",
  deckLength: number,
  mountFn: MountFn
): void {
  root.innerHTML = "";
  renderAuthHeader(root);
  const wrap = document.createElement("div");
  wrap.className = "game-wrap";
  const uiGame = getPwaUi(appLocale);
  const home = document.createElement("button");
  home.type = "button";
  home.className = "btn-secondary game-back";
  home.textContent = uiGame.backHome;
  home.addEventListener("click", () => setView("home"));
  wrap.appendChild(home);
  const progressEl = document.createElement("p");
  progressEl.className = "game-session-progress";
  progressEl.setAttribute("aria-live", "polite");
  wrap.appendChild(progressEl);
  const slot = document.createElement("div");
  slot.className = "game-slot";
  wrap.appendChild(slot);
  root.appendChild(wrap);

  let roundsCompletedInSession = 0;
  let mgSessionCorrectRounds = 0;
  let mgSessionWeightedCorrect = 0;
  const mgSessionConceptIds: string[] = [];
  let mgRoundWeighted = 0;
  let mgRoundWeightTotal = 0;

  function pickMiniGameRoundIndex(): number {
    return pickRoundIndexByMastery({
      gameType,
      deckLength,
      mastery,
      scores,
      getConceptIds: (i) => gameRoundConceptIds(gameType, i),
      targetElo: currentGm().targetEloForRounds(),
    });
  }

  function updateMiniGameProgressLabel(): void {
    progressEl.textContent = uiGame.sessionCounter(
      mgSessionCorrectRounds,
      roundsCompletedInSession
    );
    progressEl.setAttribute("aria-label", uiGame.sessionStats);
  }

  const ctx: GameContext = {
    emit: (event) => {
      mgRoundWeighted += event.weight * (event.correct ? 1 : 0);
      mgRoundWeightTotal += event.weight;
      mgSessionConceptIds.push(event.conceptId);
      const result = recordGameEvent({
        graph: g,
        player,
        mastery,
        persistence: pers,
        event,
      });
      player = result.player;
      mastery = result.mastery;
      saveMasteryState(mastery);
    },
    done: () => {
      advanceRound(gameType, deckLength);
      const roundPerfect =
        mgRoundWeightTotal > 0 && Math.abs(mgRoundWeighted - mgRoundWeightTotal) < 1e-9;
      mgSessionCorrectRounds += roundPerfect ? 1 : 0;
      mgSessionWeightedCorrect += mgRoundWeighted;
      mgRoundWeighted = 0;
      mgRoundWeightTotal = 0;

      roundsCompletedInSession++;
      if (unmountCurrentGame) {
        unmountCurrentGame();
        unmountCurrentGame = null;
      }
      if (roundsCompletedInSession >= SESSION_LENGTH) {
        miniGameEndStats = {
          gameType,
          perfectRounds: mgSessionCorrectRounds,
          weightedAccuracy: Math.round((mgSessionWeightedCorrect / SESSION_LENGTH) * 100),
          conceptIds: [...new Set(mgSessionConceptIds)],
        };
        render();
        return;
      }
      updateMiniGameProgressLabel();
      unmountCurrentGame = mountFn(slot, ctx, pickMiniGameRoundIndex());
    },
  };
  updateMiniGameProgressLabel();
  unmountCurrentGame = mountFn(slot, ctx, pickMiniGameRoundIndex());
}

function renderHome(root: HTMLElement): void {
  const ui = getPwaUi(appLocale);
  if (signedOutManually && !getCurrentSession()) {
    root.innerHTML = `
      <h1>${escapeHtml(ui.appName)}</h1>
      <div class="card auth-empty">
        <p class="auth-empty-line">${escapeHtml(ui.authSignedOutEmpty)}</p>
        <button type="button" class="auth-empty-cta">${escapeHtml(ui.authSignedOutCta)}</button>
      </div>
    `;
    renderAuthHeader(root);
    (root.querySelector(".auth-empty-cta") as HTMLButtonElement | null)?.addEventListener(
      "click",
      () => openSignInModal()
    );
    renderFooter(root);
    return;
  }
  const isSignedIn = Boolean(getCurrentSession());
  const topicTitles = pack.getCopy(appLocale).titles;
  const streak = currentStreakDays(pers.loadAttempts());
  const gm = currentGm();
  const weakest = gm.recommendConcept({ graph: g, scores, mastery });
  const weakestLabel = weakest ? (topicTitles[weakest] ?? weakest) : null;
  const recommendedView = pack.recommendViewForConcept(weakest);
  const homeHint = gm.framingFor("home");
  const homeHintText =
    homeHint === "patient" ? ui.gmHomeHintPatient : homeHint === "drill" ? ui.gmHomeHintDrill : "";
  const qp = getLinearQuestProgress(pack.quest, mastery);
  let questHtml = "";
  if (qp) {
    if (!qp.isComplete && qp.current) {
      const pathName =
        pack.id === "first_aid"
          ? ui.questPathFirstAid
          : pack.id === "ai_literacy"
            ? ui.questPathAiLiteracy
            : pack.id === "notany"
              ? ui.questPathNotany
              : pack.id === "epso_ad8_ai"
                ? ui.questPathEpsoAd8Ai
                : (pack.quest?.id ?? "path");
      const cname = topicTitles[qp.current.conceptId] ?? qp.current.conceptId;
      const stepNum = qp.activeIndex + 1;
      const line = ui.questStepLine(stepNum, qp.total, cname);
      const target = format(qp.current.minElo);
      questHtml = `<div class="quest-arc" role="status" aria-label="${escapeHtml(
        ui.questArcAria
      )}"><div class="quest-arc__head">${escapeHtml(ui.questPathLabel)} · ${escapeHtml(
        pathName
      )}</div><div class="quest-arc__step">${escapeHtml(line)}</div><div class="quest-arc__target">${escapeHtml(
        ui.questTargetHint(target)
      )}</div></div>`;
    } else if (qp.isComplete) {
      questHtml = `<div class="quest-arc quest-arc--done" role="status">${escapeHtml(
        ui.questArcComplete
      )}</div>`;
    }
  }
  const cards: Array<{ id: View; title: string; sub: string; concepts: string[] }> = [
    {
      id: "mcq",
      title: ui.cardMcqTitle,
      sub: ui.cardMcqSub,
      concepts: ["foundations", "trust_reasoning", "context", "evidence"],
    },
    {
      id: "backpack",
      title: ui.cardBackpackTitle,
      sub: ui.cardBackpackSub,
      concepts: ["prompting", "context", "system_safety"],
    },
    {
      id: "spotlie",
      title: ui.cardSpotlieTitle,
      sub: ui.cardSpotlieSub,
      concepts: ["trust_reasoning", "evidence", "model_theory"],
    },
    {
      id: "redact",
      title: ui.cardRedactTitle,
      sub: ui.cardRedactSub,
      concepts: ["privacy", "system_safety", "adversarial"],
    },
  ];

  root.innerHTML = `
    <h1>${escapeHtml(ui.appName)}</h1>
    <div class="card">
      <p class="locale-bar">
        <label for="home-subject">${escapeHtml(ui.subject)}</label>
        <select id="home-subject" aria-label="${escapeHtml(ui.subjectAria)}"></select>
        <label for="home-profile">${escapeHtml(ui.profile)}</label>
        <select id="home-profile" aria-label="${escapeHtml(ui.profileAria)}"></select>
        <label for="home-gm">${escapeHtml(ui.gameStyle)}</label>
        <select id="home-gm" aria-label="${escapeHtml(ui.gameStyleAria)}"></select>
        <label for="home-locale">${escapeHtml(ui.language)}</label>
        <select id="home-locale" aria-label="${escapeHtml(ui.languageAria)}"></select>
      </p>
      ${
        pack.id === "first_aid"
          ? `<p class="subject-note" role="note">${escapeHtml(ui.subjectFirstAidNote)}</p>`
          : pack.id === "notany"
            ? `<p class="subject-note" role="note">${escapeHtml(ui.subjectNotanyNote)}</p>`
            : pack.id === "epso_ad8_ai"
              ? `<p class="subject-note" role="note">${escapeHtml(ui.subjectEpsoAd8AiNote)}</p>`
              : ""
      }
      ${questHtml}
      ${homeHintText ? `<p class="gm-hint">${escapeHtml(homeHintText)}</p>` : ""}
      <div class="stats" aria-label="${escapeHtml(ui.profileStats)}">
        <span>${escapeHtml(ui.ratingLabel)} <strong>${format(player.rating)}</strong></span>
        ${
          gm.params.showStreak
            ? `<span aria-label="${escapeHtml(ui.streakLabelAria)}">${
                streak === 0 ? escapeHtml(ui.streakNone) : escapeHtml(ui.streakDayStreak(streak))
              }</span>`
            : ""
        }
      </div>
      ${
        weakestLabel
          ? `<div class="banner" aria-label="${escapeHtml(ui.weakestConceptAria)}">${escapeHtml(ui.focusPrefix)} <strong>${escapeHtml(weakestLabel)}</strong></div>`
          : ""
      }
      <p class="hint">${ui.pickFormatHint}</p>
      <div class="home-grid" role="list"></div>
      <div class="actions">
        ${
          isSignedIn
            ? ""
            : `<button type="button" class="btn-secondary" id="btn-reset">${escapeHtml(ui.resetProgress)}</button>`
        }
      </div>
    </div>
  `;
  renderAuthHeader(root);
  const grid = root.querySelector(".home-grid");
  if (!grid) return;
  cards.forEach((c, i) => {
    const card = document.createElement("button");
    card.type = "button";
    card.className = "home-card";
    card.setAttribute("role", "listitem");
    if (recommendedView === c.id) card.classList.add("home-card--recommended");
    card.innerHTML = `
      <span class="opt-key" aria-hidden="true">${i + 1}</span>
      <span class="home-card__title">${escapeHtml(c.title)}</span>
      <span class="home-card__sub">${escapeHtml(c.sub)}</span>
      ${recommendedView === c.id ? `<span class="home-card__badge">${escapeHtml(ui.suggestedBadge)}</span>` : ""}
    `;
    card.addEventListener("click", () => setView(c.id));
    grid.appendChild(card);
  });
  wireSubjectSelect(root.querySelector<HTMLSelectElement>("#home-subject"));
  wireProfileSelect(root.querySelector<HTMLSelectElement>("#home-profile"));
  wireGmSelect(root.querySelector<HTMLSelectElement>("#home-gm"));
  wireLocaleSelect(root.querySelector<HTMLSelectElement>("#home-locale"));
  document.getElementById("btn-reset")?.addEventListener("click", resetAll);
  renderFooter(root);
}

function nextQuestion(): void {
  showSummaryNext = false;
  const gm = currentGm();
  const rw = gm.params.recencyWindow;
  const ex = rw <= 0 ? new Set<string>() : new Set(recents);
  let qn = gm.pickNextQuestion({ bank, player, mastery, scores, excludeIds: ex, pass: "first" });
  if (!qn) {
    recents.length = 0;
    qn = gm.pickNextQuestion({
      bank,
      player,
      mastery,
      scores,
      excludeIds: new Set(),
      pass: "retry",
    });
  }
  current = qn;
  selectionLocked = false;
  if (rw > 0 && current && !recents.includes(current.id)) {
    recents.push(current.id);
    while (recents.length > rw) recents.shift();
  }
  render();
}

function renderMcq(root: HTMLElement): void {
  if (showSummaryNext) {
    renderSummary(root);
    return;
  }
  if (!current) {
    nextQuestion();
    return;
  }
  const ui = getPwaUi(appLocale);
  const topicTitles = pack.getCopy(appLocale).titles;
  const conceptLabel = topicTitles[current.conceptId] ?? current.conceptId;
  const gm = currentGm();
  const weakest = gm.recommendConcept({ graph: g, scores, mastery });
  const weakestLabel = weakest ? (topicTitles[weakest] ?? weakest) : null;
  const streak = currentStreakDays(pers.loadAttempts());
  root.innerHTML = `
    <h1>${escapeHtml(ui.appName)}</h1>
    <div class="card">
      <p class="locale-bar">
        <label for="mcq-locale">${escapeHtml(ui.language)}</label>
        <select id="mcq-locale" aria-label="${escapeHtml(ui.languageAria)}"></select>
      </p>
      <div class="stats" aria-label="${escapeHtml(ui.sessionStats)}">
        <span>${escapeHtml(ui.ratingLabel)} <strong>${format(player.rating)}</strong></span>
        <span>${escapeHtml(ui.sessionCounter(session.correct, session.total))}</span>
        ${
          gm.params.showStreak
            ? `<span aria-label="${escapeHtml(ui.streakLabelAria)}">${
                streak === 0 ? escapeHtml(ui.streakNone) : escapeHtml(ui.streakDayStreak(streak))
              }</span>`
            : ""
        }
      </div>
      ${
        weakestLabel
          ? `<div class="banner" aria-label="${escapeHtml(ui.weakestConceptAria)}">${escapeHtml(ui.focusPrefix)} <strong>${escapeHtml(weakestLabel)}</strong></div>`
          : ""
      }
      <span class="topic">${escapeHtml(conceptLabel)}</span>
      <p class="stem">${escapeHtml(current.text)}</p>
      <div class="options" id="opts" role="radiogroup" aria-label="${escapeHtml(ui.answerOptionsAria)}"></div>
      <p class="feedback" id="fb" role="status" aria-live="polite"></p>
      <div class="actions" id="actions"></div>
      <p class="hint">${ui.hintMcq}</p>
    </div>
    <p class="footer">${ui.footer}</p>
  `;
  renderAuthHeader(root);
  const opts = document.getElementById("opts");
  if (!opts) return;
  wireLocaleSelect(root.querySelector<HTMLSelectElement>("#mcq-locale"));
  [0, 1, 2, 3].forEach((i) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "opt";
    b.setAttribute("role", "radio");
    b.setAttribute("aria-checked", "false");
    b.innerHTML = `<span class="opt-key" aria-hidden="true">${i + 1}</span> ${escapeHtml(current!.options[i as 0 | 1 | 2 | 3])}`;
    b.disabled = selectionLocked;
    b.addEventListener("click", () => onPick(i as 0 | 1 | 2 | 3, opts!));
    opts.appendChild(b);
  });
  const act = document.getElementById("actions");
  if (act) renderActions(act);
}

function renderActions(act: HTMLElement): void {
  const ui = getPwaUi(appLocale);
  if (selectionLocked) {
    const nextBtn = document.createElement("button");
    nextBtn.type = "button";
    nextBtn.className = "btn-primary";
    nextBtn.textContent = session.total >= SESSION_LENGTH ? ui.seeSummary : ui.nextQuestion;
    nextBtn.addEventListener("click", () => {
      if (session.total >= SESSION_LENGTH) {
        showSummaryNext = true;
        render();
      } else {
        nextQuestion();
      }
    });
    act.appendChild(nextBtn);
  }
  const home = document.createElement("button");
  home.type = "button";
  home.className = "btn-secondary";
  home.textContent = ui.home;
  home.addEventListener("click", () => setView("home"));
  act.appendChild(home);
  if (!getCurrentSession()) {
    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "btn-secondary";
    reset.textContent = ui.resetProgress;
    reset.addEventListener("click", resetAll);
    act.appendChild(reset);
  }
}

function renderSummary(root: HTMLElement): void {
  const ui = getPwaUi(appLocale);
  const gm = currentGm();
  const topicTitles = pack.getCopy(appLocale).titles;
  const accuracy = session.total > 0 ? Math.round((session.correct / session.total) * 100) : 0;
  const concepts = Array.from(new Set(session.conceptIds));
  const conceptLines = concepts
    .slice(0, 6)
    .map((id) => `<li>${escapeHtml(topicTitles[id] ?? id)}</li>`)
    .join("");
  const streak = currentStreakDays(pers.loadAttempts());
  root.innerHTML = `
    <h1>${escapeHtml(ui.appName)}</h1>
    <div class="card">
      <p class="locale-bar">
        <label for="summary-locale">${escapeHtml(ui.language)}</label>
        <select id="summary-locale" aria-label="${escapeHtml(ui.languageAria)}"></select>
      </p>
      <span class="topic">${escapeHtml(ui.sessionSummary)}</span>
      <p class="stem">${escapeHtml(ui.summaryScoreLine(session.correct, session.total, accuracy))}</p>
      <ul class="concepts">${conceptLines}</ul>
      <div class="stats" aria-label="${escapeHtml(ui.finalStats)}">
        <span>${escapeHtml(ui.ratingLabel)} <strong>${format(player.rating)}</strong></span>
        ${
          gm.params.showStreak
            ? `<span aria-label="${escapeHtml(ui.streakLabelAria)}">${
                streak === 0 ? escapeHtml(ui.streakNone) : escapeHtml(ui.streakDayStreak(streak))
              }</span>`
            : ""
        }
      </div>
      <div class="actions">
        <button type="button" class="btn-primary" id="btn-new">${escapeHtml(ui.newSession)}</button>
        <button type="button" class="btn-secondary" id="btn-home">${escapeHtml(ui.home)}</button>
        ${
          getCurrentSession()
            ? ""
            : `<button type="button" class="btn-secondary" id="btn-reset">${escapeHtml(ui.resetProgress)}</button>`
        }
      </div>
      <p class="hint">${ui.hintSummary}</p>
    </div>
  `;
  renderAuthHeader(root);
  wireLocaleSelect(root.querySelector<HTMLSelectElement>("#summary-locale"));
  document.getElementById("btn-new")?.addEventListener("click", startNewSession);
  document.getElementById("btn-home")?.addEventListener("click", () => setView("home"));
  document.getElementById("btn-reset")?.addEventListener("click", resetAll);
}

function renderMiniGameSummary(root: HTMLElement, stats: MiniGameEndStats): void {
  const ui = getPwaUi(appLocale);
  const gm = currentGm();
  const topicTitles = pack.getCopy(appLocale).titles;
  const concepts = Array.from(new Set(stats.conceptIds));
  const conceptLines = concepts
    .slice(0, 6)
    .map((id) => `<li>${escapeHtml(topicTitles[id] ?? id)}</li>`)
    .join("");
  const streak = currentStreakDays(pers.loadAttempts());
  const detail = ui.miniGameSummaryDetail(
    stats.perfectRounds,
    SESSION_LENGTH,
    stats.weightedAccuracy
  );
  root.innerHTML = `
    <h1>${escapeHtml(ui.appName)}</h1>
    <div class="card">
      <p class="locale-bar">
        <label for="mini-summary-locale">${escapeHtml(ui.language)}</label>
        <select id="mini-summary-locale" aria-label="${escapeHtml(ui.languageAria)}"></select>
      </p>
      <span class="topic">${escapeHtml(ui.sessionSummary)}</span>
      <p class="stem">${escapeHtml(detail)}</p>
      <ul class="concepts">${conceptLines}</ul>
      <div class="stats" aria-label="${escapeHtml(ui.finalStats)}">
        <span>${escapeHtml(ui.ratingLabel)} <strong>${format(player.rating)}</strong></span>
        ${
          gm.params.showStreak
            ? `<span aria-label="${escapeHtml(ui.streakLabelAria)}">${
                streak === 0 ? escapeHtml(ui.streakNone) : escapeHtml(ui.streakDayStreak(streak))
              }</span>`
            : ""
        }
      </div>
      <div class="actions">
        <button type="button" class="btn-primary" id="btn-mini-new">${escapeHtml(ui.newSession)}</button>
        <button type="button" class="btn-secondary" id="btn-mini-home">${escapeHtml(ui.home)}</button>
        ${
          getCurrentSession()
            ? ""
            : `<button type="button" class="btn-secondary" id="btn-mini-reset">${escapeHtml(ui.resetProgress)}</button>`
        }
      </div>
      <p class="hint">${ui.hintSummary}</p>
    </div>
  `;
  renderAuthHeader(root);
  wireLocaleSelect(root.querySelector<HTMLSelectElement>("#mini-summary-locale"));
  document.getElementById("btn-mini-new")?.addEventListener("click", () => {
    miniGameEndStats = null;
    render();
  });
  document.getElementById("btn-mini-home")?.addEventListener("click", () => setView("home"));
  document.getElementById("btn-mini-reset")?.addEventListener("click", resetAll);
}

function startNewSession(): void {
  session = freshSession();
  saveSession(session);
  showSummaryNext = false;
  nextQuestion();
}

function emojiForDelta(correct: boolean, delta: number): string {
  if (correct) {
    if (delta >= 28) return "🕺";
    if (delta >= 22) return "🚀";
    if (delta >= 15) return "🔥";
    if (delta >= 5) return "✅";
    return "👍";
  }
  if (delta > -5) return "😕";
  if (delta > -15) return "😬";
  return "🥶";
}

function spawnEmojiBurst(emoji: string, sourceEl: Element): void {
  const rect = sourceEl.getBoundingClientRect();
  const span = document.createElement("span");
  span.className = "emoji-burst";
  span.textContent = emoji;
  span.style.left = `${rect.left + rect.width / 2}px`;
  span.style.top = `${rect.top + rect.height / 2}px`;
  document.body.appendChild(span);
  span.addEventListener("animationend", () => span.remove(), { once: true });
}

function onPick(choice: 0 | 1 | 2 | 3, container: HTMLElement): void {
  if (selectionLocked || !current) return;
  const prevRating = player.rating;
  const result = applyAnswer(player, current, choice);
  selectionLocked = true;
  const correct = result.correct;
  const ratingDelta = result.player.rating - prevRating;
  const emoji = emojiForDelta(correct, ratingDelta);
  mastery = updateMasteryAfterAttempt(g, mastery, current.conceptId, correct);
  saveMasteryState(mastery);
  player = result.player;
  bank = withUpdatedQuestion(bank, result.question);
  pers.savePlayer(player);
  pers.saveQuestionBank(bank);
  pers.appendAttempt({
    at: new Date().toISOString(),
    questionId: current.id,
    conceptId: current.conceptId,
    correct,
    playerRatingAfter: player.rating,
    gameType: "mcq",
  });
  session = {
    ...session,
    total: session.total + 1,
    correct: session.correct + (correct ? 1 : 0),
    conceptIds: [...session.conceptIds, current.conceptId],
  };
  saveSession(session);
  const pickedBtn = container.children[choice] as HTMLButtonElement | undefined;
  for (let nIdx = 0; nIdx < container.children.length; nIdx++) {
    const btn = container.children[nIdx] as HTMLButtonElement;
    btn.disabled = true;
    if (nIdx === result.question.correctIndex) btn.classList.add("correct");
    else if (nIdx === choice) btn.classList.add("wrong");
  }
  if (pickedBtn) spawnEmojiBurst(emoji, pickedBtn);
  const fb = document.getElementById("fb");
  if (fb) {
    const ui = getPwaUi(appLocale);
    fb.textContent = `${emoji} ${correct ? ui.feedbackCorrect : ui.feedbackWrong}`;
    fb.className = "feedback " + (correct ? "ok" : "bad") + " pop";
    fb.addEventListener("animationend", () => fb.classList.remove("pop"), { once: true });
  }
  const act = document.getElementById("actions");
  if (act) {
    act.innerHTML = "";
    renderActions(act);
  }
}

function resetAll(): void {
  const savedLocale: Locale = appLocale;
  const savedGm = characterPreferences.gmId;
  const savedSubject = pack.id;
  if (typeof localStorage !== "undefined") {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith("notanyai:") || k.startsWith("ai-prepper:")) localStorage.removeItem(k);
    }
    saveActiveCharacterId(DEFAULT_CHARACTER_ID);
    saveActiveSubjectId(savedSubject);
  }
  const p = getSubjectPack(savedSubject) ?? getSubjectPack(defaultSubjectId())!;
  pack = p;
  g = pack.buildGraph();
  scores = precomputeGraphScores(g);
  rebindPersistenceForSubject();
  characterPreferences = {
    locale: savedLocale,
    gmId: savedGm,
    subjectId: pack.id,
  };
  writeCharacterPreferencesJson(persistencePrefix, characterPreferences);
  appLocale = characterPreferences.locale;
  player = createPlayer(1200);
  bank = buildBlendedBank(null);
  mastery = createMasteryState(pack.conceptIds);
  session = freshSession();
  recents.length = 0;
  pers.savePlayer(player);
  pers.saveQuestionBank(bank);
  saveMasteryState(mastery);
  saveSession(session);
  current = null;
  showSummaryNext = false;
  miniGameEndStats = null;
  setView("home");
}

function format(r: number): string {
  return r.toFixed(0);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function handleKeydown(ev: KeyboardEvent): void {
  if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
  const target = ev.target as HTMLElement | null;
  if (
    target &&
    (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT")
  )
    return;
  const key = ev.key.toLowerCase();
  if (
    miniGameEndStats &&
    (currentView === "backpack" || currentView === "spotlie" || currentView === "redact") &&
    key === "n"
  ) {
    ev.preventDefault();
    miniGameEndStats = null;
    render();
    return;
  }
  if (key === "h" && currentView !== "home") {
    ev.preventDefault();
    setView("home");
    return;
  }
  if (currentView === "home") {
    const idx = ["1", "2", "3", "4"].indexOf(ev.key);
    if (idx >= 0) {
      ev.preventDefault();
      const order: View[] = ["mcq", "backpack", "spotlie", "redact"];
      setView(order[idx]!);
    }
    return;
  }
  if (currentView !== "mcq") return;
  if (showSummaryNext) {
    if (key === "n") {
      ev.preventDefault();
      startNewSession();
    }
    return;
  }
  if (!current) return;
  if (["1", "2", "3", "4"].includes(ev.key) && !selectionLocked) {
    ev.preventDefault();
    const opts = document.getElementById("opts");
    if (!opts) return;
    onPick((Number(ev.key) - 1) as 0 | 1 | 2 | 3, opts);
    return;
  }
  if (key === "n" && selectionLocked) {
    ev.preventDefault();
    if (session.total >= SESSION_LENGTH) {
      showSummaryNext = true;
      render();
    } else {
      nextQuestion();
    }
  }
}

// Always end up in a rendered state. If the persistence rebuild throws
// (network blip, schema drift, RLS violation), fall back to a local-only
// `pers` and surface a toast — never leave the page blank.
async function safeRebuildAndRender(authSession: Session | null): Promise<void> {
  try {
    await rebuildPersistence(authSession);
  } catch (err) {
    if (typeof console !== "undefined") {
      console.error("[notanyai] persistence rebuild failed; using local-only:", err);
    }
    pers = new LocalStoragePersistence(persistencePrefix);
    player = pers.loadPlayer() ?? createPlayer(1200);
    bank = buildBlendedBank(pers.loadQuestionBank());
    pers.saveQuestionBank(bank);
    mastery = loadMasteryState();
    session = loadSession();
    showToast(getPwaUi(appLocale).authSyncFailedToast);
  }
  render();
}

// Each step is independently wrapped so one failure doesn't take down the rest.
// Failures here are non-fatal: state stays at the safe module-top default and
// the page still renders.
function loadPersistedState(): void {
  try {
    const loaded = pers.loadPlayer();
    if (loaded) player = loaded;
  } catch (err) {
    logBootStateFailure("player", err);
  }
  try {
    bank = buildBlendedBank(pers.loadQuestionBank());
    pers.saveQuestionBank(bank);
  } catch (err) {
    logBootStateFailure("bank", err);
  }
  try {
    mastery = loadMasteryState();
  } catch (err) {
    logBootStateFailure("mastery", err);
  }
  try {
    session = loadSession();
  } catch (err) {
    logBootStateFailure("session", err);
  }
  try {
    currentView = loadView();
  } catch (err) {
    logBootStateFailure("view", err);
  }
}

function logBootStateFailure(kind: string, err: unknown): void {
  if (typeof console === "undefined") return;
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  console.warn(`[notanyai] boot: ${kind} load failed; using default. ${msg}`);
}

function boot(): void {
  loadPersistedState();
  document.addEventListener("keydown", handleKeydown);
  if (!snapshotWriteFailureListenerBound && typeof window !== "undefined") {
    snapshotWriteFailureListenerBound = true;
    window.addEventListener("snapshot:writeFailed", () => {
      showToast("Failed to save your last move. We'll keep retrying.");
    });
  }
  if (typeof window !== "undefined") {
    window.addEventListener("hashchange", () => {
      const next = readHashView();
      if (next && next !== currentView) setView(next);
      else if (
        !next &&
        (currentView === "privacy" || currentView === "settings" || currentView === "about")
      ) {
        setView("home");
      }
    });
  }
  const initialHashView = readHashView();
  if (initialHashView) currentView = initialHashView;
  pendingAuthError = consumeAuthHashError();
  // Render the local/guest UI immediately so a slow or failing Supabase
  // init can never leave the page blank. The signed-in rebuild below
  // re-renders once the session resolves.
  render();
  void initSessionStore()
    .catch((err) => {
      if (typeof console !== "undefined") {
        console.error("[notanyai] initSessionStore failed:", err);
      }
      return null;
    })
    .then(() => safeRebuildAndRender(getCurrentSession()))
    .finally(() => {
      subscribeToSession((nextSession) => {
        void safeRebuildAndRender(nextSession);
      });
      if (pendingAuthError && !getCurrentSession()) {
        const ui = getPwaUi(appLocale);
        openSignInModal(ui.authAuthFailedExpired);
      }
    });
}

boot();
